import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useQueryParams } from "@/hooks/use-query-param";
import { nameIssue } from "@/constants/validation-constant";
import { useSnackbar } from "@/hooks/useSnackbar";
import {
  getErrorSnackbar,
  getSuccessSnackbar,
  getXtenantId,
} from "@/utils/utils";
import { HistoryRow } from "../readmission-f2f-types";
import {
  createHistoryRow,
  isSameHistoryRow,
  recordChangedSnackbar,
} from "../readmission-f2f-utils";

export interface HistoryQueryContext {
  patientId?: string;
  appointmentId?: string;
  xTENANTID?: string;
}

export interface HistoryMutationContext extends HistoryQueryContext {
  row: HistoryRow;
}

export interface HistorySectionAdapter {
  listKey: string;
  recordName: string;
  isSaving: boolean;
  fetchRows: (ctx: HistoryQueryContext) => Promise<HistoryRow[]>;
  createEntry: (ctx: HistoryMutationContext) => Promise<unknown>;
  updateEntry: (ctx: HistoryMutationContext) => Promise<unknown>;
  archiveEntry: (ctx: { uuid: string; xTENANTID?: string }) => Promise<unknown>;
}

export interface UseHistorySection {
  rows: HistoryRow[];
  draft: HistoryRow;
  /** Set when Add was pressed with an invalid name; cleared on the next edit. */
  draftError?: string;
  isLoading: boolean;
  isSaving: boolean;
  updateRow: (id: string, patch: Partial<HistoryRow>) => void;
  updateDraft: (patch: Partial<HistoryRow>) => void;
  addDraft: () => Promise<void>;
  saveRow: (row: HistoryRow) => Promise<void>;
  removeRow: (row: HistoryRow) => void;
}

export const useHistorySection = (
  adapter: HistorySectionAdapter
): UseHistorySection => {
  const adapterRef = useRef(adapter);
  adapterRef.current = adapter;

  const { patientId } = useParams() as { patientId?: string };
  const appointmentId: any = useQueryParams("appointmentid");
  const xTENANTID = getXtenantId();
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();

  const { listKey } = adapter;

  const { data: serverContent, isLoading } = useQuery({
    queryKey: [listKey, patientId, appointmentId, xTENANTID],
    refetchOnWindowFocus: false,
    enabled: !!patientId || !!appointmentId,
    queryFn: () =>
      adapterRef.current.fetchRows({ patientId, appointmentId, xTENANTID }),
  });

  const serverRows = useMemo(() => serverContent ?? [], [serverContent]);
  const serverById = useMemo(() => {
    const map = new Map<string, HistoryRow>();
    serverRows.forEach((row) => map.set(row.id, row));
    return map;
  }, [serverRows]);

  const [rows, setRows] = useState<HistoryRow[]>([]);
  const signature = useMemo(
    () => serverRows.map((r) => `${r.uuid}|${r.name}|${r.notes}`).join("~"),
    [serverRows]
  );
  const lastSignature = useRef<string | null>(null);
  useEffect(() => {
    if (lastSignature.current !== signature) {
      lastSignature.current = signature;
      setRows(serverRows);
    }
  }, [signature, serverRows]);

  const invalidate = useCallback(
    () => queryClient.invalidateQueries({ queryKey: [listKey] }),
    [queryClient, listKey]
  );

  const updateRow = useCallback((id: string, patch: Partial<HistoryRow>) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, ...patch } : row))
    );
  }, []);

  const [draft, setDraft] = useState<HistoryRow>(() => createHistoryRow());
  const [draftError, setDraftError] = useState<string>();

  const updateDraft = useCallback((patch: Partial<HistoryRow>) => {
    setDraft((prev) => ({ ...prev, ...patch }));
    if (patch.name !== undefined) setDraftError(undefined);
  }, []);

  const addDraft = useCallback(async () => {
    const issue = nameIssue(draft.name);
    if (issue) {
      setDraftError(issue);
      return;
    }
    try {
      await adapterRef.current.createEntry({
        row: draft,
        patientId,
        appointmentId,
        xTENANTID,
      });
      showSnackbar(
        recordChangedSnackbar(adapterRef.current.recordName, "added")
      );
      setDraft(createHistoryRow());
      await invalidate();
    } catch (err) {
      showSnackbar(getErrorSnackbar(err));
    }
  }, [draft, patientId, appointmentId, xTENANTID, invalidate, showSnackbar]);

  const saveRow = useCallback(
    async (row: HistoryRow) => {
      if (!row.uuid || nameIssue(row.name)) return;
      const original = serverById.get(row.id);
      if (original && isSameHistoryRow(original, row)) return;
      try {
        const res = await adapterRef.current.updateEntry({
          row,
          patientId,
          appointmentId,
          xTENANTID,
        });
        showSnackbar(getSuccessSnackbar(res));
        await invalidate();
      } catch (err) {
        showSnackbar(getErrorSnackbar(err));
      }
    },
    [serverById, patientId, appointmentId, xTENANTID, invalidate, showSnackbar]
  );

  const removeRow = useCallback(
    async (row: HistoryRow) => {
      if (!row.uuid) {
        setRows((prev) => prev.filter((it) => it.id !== row.id));
        return;
      }
      try {
        await adapterRef.current.archiveEntry({
          uuid: row.uuid,
          xTENANTID,
        });
        showSnackbar(
          recordChangedSnackbar(adapterRef.current.recordName, "removed")
        );
        await invalidate();
      } catch (err) {
        showSnackbar(getErrorSnackbar(err));
      }
    },
    [xTENANTID, invalidate, showSnackbar]
  );

  return {
    rows,
    draft,
    draftError,
    isLoading,
    isSaving: adapter.isSaving,
    updateRow,
    updateDraft,
    addDraft,
    saveRow,
    removeRow,
  };
};
